import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { apiFetch } from '../../../lib/api';
import { useSediUtente } from '../../../hooks/useSediUtente';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { formatNumber } from '../../../utils/formatters';
import NominativoClientePicker from '../../../components/NominativoClientePicker';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';

// ── Tipi ─────────────────────────────────────────────────────────────────────

type StatoCommessa = 'BOZZA' | 'IN_CORSO' | 'COMPLETATA' | 'CHIUSA';

interface Commessa {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
  stato: StatoCommessa;
  societaId: number;
  societaNome?: string;
  sedeId: number;
  sedeNome?: string;
  responsabileId: number;
  responsabileNome?: string;
  budgetIniziale?: number | null;
  dataApertura: string;
  dataChiusura?: string | null;
  nOrdini?: number;
  nSal?: number;
  totaleOrdini?: number;
  totaleSal?: number;
}

interface Sede     { id: number; nome: string; societaId: number; sedeCommerciale: boolean; }
interface Utente   { id: number; nome: string; }
interface CodiceIvaOption { id: number; codice: string; descrizione: string; aliquota: string; predefinito?: boolean; }

interface FormCommessa {
  societaId: number;
  sedeId: number;
  nominativoId: number | null;
  descrizione: string;
  indirizzoLavori: string;
  responsabileId: number;
  budgetIniziale: string;
  codiceIvaDefaultId: number | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number | string | null | undefined) =>
  n == null ? '—' : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(n));

const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('it-IT') : '—';

const trunc = (s: string | null | undefined, n: number) =>
  s && s.length > n ? s.slice(0, n) + '…' : s ?? '—';

const STATO_CONFIG: Record<StatoCommessa, { label: string; cls: string; dotColor: string }> = {
  BOZZA:      { label: 'Bozza',      cls: 'bop-badge--neutral',  dotColor: '#94a3b8' },
  IN_CORSO:   { label: 'In Corso',   cls: 'bop-badge--info',     dotColor: '#3b82f6' },
  COMPLETATA: { label: 'Completata', cls: 'bop-badge--success',  dotColor: '#22c55e' },
  CHIUSA:     { label: 'Chiusa',     cls: 'bop-commesse__badge-chiusa', dotColor: '#1e293b' },
};

const TRANSIZIONI: Record<StatoCommessa, StatoCommessa[]> = {
  BOZZA:      ['IN_CORSO'],
  IN_CORSO:   ['COMPLETATA', 'CHIUSA'],
  COMPLETATA: ['CHIUSA'],
  CHIUSA:     [],
};

// ── Componente principale ─────────────────────────────────────────────────────

export default function CommessePage() {
  const navigate = useNavigate();

  const [items,    setItems]    = useState<Commessa[]>([]);
  const [sedi,     setSedi]     = useState<Sede[]>([]);
  const [utenti,   setUtenti]   = useState<Utente[]>([]);
  const [utentiForm, setUtentiForm] = useState<Utente[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  const {
    societaId: fSocietaId,
    setSocietaId: setFSocietaId,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Filtri
  const [fSedeId,    setFSedeId]    = useState('');
  const [fStato,     setFStato]     = useState('');
  const [fRespId,    setFRespId]    = useState('');
  const [fSearch,    setFSearch]    = useState('');

  // Sedi filtrate per società selezionata
  const sediFiltrate = fSocietaId
    ? sedi.filter(s => s.societaId === Number(fSocietaId))
    : sedi;

  // Modal Nuova Commessa
  const [modalNuova, setModalNuova] = useState(false);
  const [form, setForm] = useState<FormCommessa>({
    societaId: 0, sedeId: 0, nominativoId: null,
    descrizione: '', indirizzoLavori: '', responsabileId: 0, budgetIniziale: '',
    codiceIvaDefaultId: null,
  });
  const [codiciIva, setCodiciIva] = useState<CodiceIvaOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  // Modal Cambia Stato
  const [modalStato, setModalStato] = useState<Commessa | null>(null);
  const [nuovoStato, setNuovoStato] = useState<StatoCommessa | ''>('');
  const [notaStato,  setNotaStato]  = useState('');
  const [savingStato, setSavingStato] = useState(false);

  // ── Caricamento ─────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!pronta) return;
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams();
      if (fSocietaId) p.set('societaId', fSocietaId);
      if (fSedeId)    p.set('sedeId',    fSedeId);
      if (fStato)     p.set('stato',     fStato);
      if (fSearch)    p.set('search',    fSearch);

      const sqSocieta = fSocietaId ? `?societaId=${fSocietaId}` : '';
      const [cRes, seRes, uRes] = await Promise.all([
        apiFetch<{ commesse: Commessa[] }>(`/api/commesse?${p}`),
        apiFetch<{ sedi: Sede[] }>(`/api/sedi${sqSocieta}`),
        // Filtro responsabile: solo con una società selezionata
        // @api GET /api/utenti/selezionabili?societaId={id} -> {"success":"true","data":[{"id":"number","nome":"string"}]}
        fSocietaId
          ? apiFetch<{ success: boolean; data: Utente[] }>(`/api/utenti/selezionabili?societaId=${fSocietaId}`)
          : Promise.resolve({ success: true, data: [] as Utente[] }),
      ]);
      setItems(cRes.commesse   ?? []);
      setSedi(seRes.sedi       ?? []);
      setUtenti(uRes.data      ?? []);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [pronta, fSocietaId, fSedeId, fStato, fSearch]);

  useEffect(() => { load(); }, [load]);

  // ── Nuova Commessa ──────────────────────────────────────────────────────────

  const apriNuova = () => {
    const socId = societa[0]?.id ?? 0;
    setForm({ societaId: socId, sedeId: 0, nominativoId: null,
              descrizione: '', indirizzoLavori: '', responsabileId: 0, budgetIniziale: '',
              codiceIvaDefaultId: null });
    setModalError(''); setModalNuova(true);
  };

  // Carica codici IVA attivi della società corrente del form
  useEffect(() => {
    if (!form.societaId) { setCodiciIva([]); return; }
    // @api GET /api/codici-iva?societaId={id}&attivo=true -> {"codici":[{"id":"number","codice":"string","descrizione":"string","aliquota":"string","attivo":"boolean","predefinito":"boolean"}]}
    apiFetch<{ codici: CodiceIvaOption[] }>(`/api/codici-iva?societaId=${form.societaId}&attivo=true`)
      .then(r => {
        const lista = r.codici || [];
        setCodiciIva(lista);
        // Auto-precompila IVA predefinita se non già scelta
        setForm(f => {
          if (f.codiceIvaDefaultId) return f;
          const pre = lista.find(c => c.predefinito);
          return pre ? { ...f, codiceIvaDefaultId: pre.id } : f;
        });
      })
      .catch(() => setCodiciIva([]));
  }, [form.societaId]);

  // Sedi commerciali visibili per l'utente — rispetta il filtro operativo (useSediUtente)
  const { sedi: sediCommerciali, hasSediMultiple: hasSediMultipleForm } =
    useSediUtente(form.societaId || null, true);

  // Auto-seleziona se l'utente ha una sola sede disponibile
  useEffect(() => {
    if (!modalNuova) return;
    if (sediCommerciali.length === 1 && form.sedeId !== sediCommerciali[0].id) {
      setForm(f => ({ ...f, sedeId: sediCommerciali[0].id }));
    }
  }, [sediCommerciali, modalNuova]); // eslint-disable-line react-hooks/exhaustive-deps

  // Responsabili selezionabili nella società del form Nuova Commessa
  useEffect(() => {
    if (!modalNuova || !form.societaId) { setUtentiForm([]); return; }
    // @api GET /api/utenti/selezionabili?societaId={id} -> {"success":"true","data":[{"id":"number","nome":"string"}]}
    apiFetch<{ success: boolean; data: Utente[] }>(`/api/utenti/selezionabili?societaId=${form.societaId}`)
      .then(r => setUtentiForm(r.data ?? []))
      .catch((e: any) => {
        console.error('[CommessePage][utentiForm]', e);
        setUtentiForm([]);
        setModalError(`Impossibile caricare i responsabili: ${e.message}`);
      });
  }, [modalNuova, form.societaId]);

  const salva = async () => {
    if (!form.nominativoId)                 { setModalError('Seleziona un cliente'); return; }
    if (!form.descrizione.trim())           { setModalError('Descrizione obbligatoria'); return; }
    if (!form.sedeId)                       { setModalError('Seleziona una sede commerciale'); return; }
    if (!form.responsabileId)               { setModalError('Seleziona un responsabile'); return; }
    if (!form.codiceIvaDefaultId)           { setModalError('Seleziona un codice IVA default'); return; }
    setSaving(true); setModalError('');
    try {
      // @api POST /api/commesse {"societaId":"number","sedeId":"number","nominativoId":"number","descrizione":"string","indirizzoLavori":"string?","responsabileId":"number","budgetIniziale":"number?","codiceIvaDefaultId":"number"} -> {"id":"number","numero":"string"}
      const nuova = await apiFetch<Commessa>('/api/commesse', {
        method: 'POST',
        body: JSON.stringify({
          societaId:            form.societaId || (societa[0]?.id ?? 0),
          sedeId:               Number(form.sedeId),
          nominativoId:         form.nominativoId,
          descrizione:          form.descrizione.trim(),
          indirizzoLavori:      form.indirizzoLavori.trim() || null,
          responsabileId:       Number(form.responsabileId),
          budgetIniziale:       form.budgetIniziale ? Number(form.budgetIniziale) : null,
          codiceIvaDefaultId:   Number(form.codiceIvaDefaultId),
        }),
      });
      setModalNuova(false);
      navigate(`/commerciale/commesse/${nuova.id}`);
    } catch (e: any) { setModalError(e.message); }
    finally { setSaving(false); }
  };

  // ── Cambia Stato ────────────────────────────────────────────────────────────

  const confermaCambiaStato = async () => {
    if (!modalStato || !nuovoStato) return;
    setSavingStato(true);
    try {
      await apiFetch(`/api/commesse/${modalStato.id}/stato`, {
        method: 'PUT',
        body: JSON.stringify({ stato: nuovoStato }),
      });
      setModalStato(null); load();
    } catch (e: any) { setModalError(e.message); }
    finally { setSavingStato(false); }
  };

  // ── Progress bar helper ──────────────────────────────────────────────────────

  const AvanzamentoBadge = ({ c }: { c: Commessa }) => {
    const tot = Number(c.totaleOrdini ?? 0);
    const sal = Number(c.totaleSal ?? 0);
    const pct = tot > 0 ? Math.min(100, Math.round((sal / tot) * 100)) : 0;
    return (
      <div className="bop-commesse__progress-wrap">
        <div className="bop-commesse__progress-label">
          <span>{pct}%</span>
        </div>
        <div className="bop-commesse__progress-bar">
          {/* Dynamic width requires inline style */}
          <div className={`bop-commesse__progress-fill ${pct >= 100 ? 'bop-commesse__progress-fill--complete' : ''}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  // Applica filtro Responsabile (client-side già prima del refactor)
  const itemsFiltered = useMemo(
    () => fRespId ? items.filter(c => c.responsabileId === Number(fRespId)) : items,
    [items, fRespId],
  );

  // Definizione colonne BopExcelTable
  const columns = useMemo<BopExcelColumn<Commessa>[]>(() => {
    const sediOpts = Array.from(new Set(items.map(c => c.sedeNome).filter(Boolean))) as string[];
    const respOpts = Array.from(new Set(items.map(c => c.responsabileNome).filter(Boolean))) as string[];
    return [
      {
        key: 'numero',
        header: 'Numero',
        accessor: (c) => c.numero,
        filter: { type: 'text' },
        width: '120px',
        render: (c) => <span className="bop-commesse__td-numero">{c.numero}</span>,
      },
      {
        key: 'cliente',
        header: 'Cliente',
        accessor: (c) => c.clienteRagioneSociale,
        filter: { type: 'text' },
        cellClassName: 'bop-commesse__td-cliente',
      },
      {
        key: 'descrizione',
        header: 'Descrizione',
        accessor: (c) => c.descrizione,
        filter: { type: 'text' },
        render: (c) => (
          <span className="bop-commesse__td-desc" title={c.descrizione}>
            {trunc(c.descrizione, 40)}
          </span>
        ),
      },
      {
        key: 'sede',
        header: 'Sede',
        accessor: (c) => c.sedeNome || '',
        filter: { type: 'enum', options: sediOpts.map(s => ({ value: s, label: s })) },
        cellClassName: 'bop-commesse__td-secondary',
        render: (c) => c.sedeNome ?? '—',
      },
      {
        key: 'responsabile',
        header: 'Responsabile',
        accessor: (c) => c.responsabileNome || '',
        filter: { type: 'enum', options: respOpts.map(r => ({ value: r, label: r })) },
        cellClassName: 'bop-commesse__td-small',
        render: (c) => c.responsabileNome ?? '—',
      },
      {
        key: 'stato',
        header: 'Stato',
        accessor: (c) => c.stato,
        filter: {
          type: 'enum',
          options: (['BOZZA','IN_CORSO','COMPLETATA','CHIUSA'] as StatoCommessa[])
            .map(s => ({ value: s, label: STATO_CONFIG[s].label })),
        },
        width: '120px',
        render: (c) => {
          const sc = STATO_CONFIG[c.stato] ?? STATO_CONFIG.BOZZA;
          return <span className={`bop-badge ${sc.cls}`}>{sc.label}</span>;
        },
      },
      {
        key: 'totaleOrdini',
        header: 'Ordini',
        accessor: (c) => c.totaleOrdini ?? null,
        filter: { type: 'number' },
        width: '110px',
        headerClassName: 'bop-text-right',
        cellClassName: 'bop-text-right bop-commesse__td-mono',
        render: (c) => fmt(c.totaleOrdini),
      },
      {
        key: 'totaleSal',
        header: 'SAL App.',
        accessor: (c) => c.totaleSal ?? null,
        filter: { type: 'number' },
        width: '110px',
        headerClassName: 'bop-text-right',
        cellClassName: 'bop-text-right bop-commesse__td-mono',
        render: (c) => fmt(c.totaleSal),
      },
      {
        key: 'avanzamento',
        header: 'Avanz.',
        accessor: (c) => {
          const tot = Number(c.totaleOrdini ?? 0);
          const sal = Number(c.totaleSal ?? 0);
          return tot > 0 ? Math.min(100, Math.round((sal / tot) * 100)) : 0;
        },
        filter: { type: 'number' },
        width: '110px',
        headerClassName: 'bop-commesse__th-avanz',
        render: (c) => <AvanzamentoBadge c={c} />,
      },
      {
        key: 'dataApertura',
        header: 'Apertura',
        accessor: (c) => c.dataApertura || '',
        filter: { type: 'dateRange' },
        width: '110px',
        cellClassName: 'bop-commesse__td-secondary',
        render: (c) => fmtDate(c.dataApertura),
      },
    ];
  }, [items]);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Gestione Commesse</h2>
          <p>{items.length} commessa{items.length !== 1 ? 'e' : ''} trovata{items.length !== 1 ? '' : ''}</p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={apriNuova}>+ Nuova Commessa</button>
      </div>

      {/* Filtri */}
      <div className="bop-commesse__filters">
        {!isUnica && (
          <select className="bop-input bop-commesse__filter-select"
            value={fSocietaId} onChange={e => { setFSocietaId(e.target.value); setFSedeId(''); }}>
            <option value="">Tutte le società</option>
            {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <select className="bop-input bop-commesse__filter-select"
          value={fSedeId} onChange={e => setFSedeId(e.target.value)}>
          <option value="">Tutte le sedi</option>
          {sediFiltrate.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
        <select className="bop-input bop-commesse__filter-select-sm"
          value={fStato} onChange={e => setFStato(e.target.value)}>
          <option value="">Tutti gli stati</option>
          <option value="BOZZA">Bozza</option>
          <option value="IN_CORSO">In Corso</option>
          <option value="COMPLETATA">Completata</option>
          <option value="CHIUSA">Chiusa</option>
        </select>
        <select className="bop-input bop-commesse__filter-select"
          value={fRespId} onChange={e => setFRespId(e.target.value)}>
          <option value="">Tutti i responsabili</option>
          {utenti.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
        </select>
        <input className="bop-input bop-commesse__filter-search" placeholder="Cerca numero, cliente, descrizione…"
          value={fSearch} onChange={e => setFSearch(e.target.value)} />
      </div>

      {error && <div className="inline-error">{error}</div>}

      <BopExcelTable<Commessa>
        data={itemsFiltered}
        loading={loading}
        rowKey={(c) => c.id}
        onRowClick={(c) => navigate(`/commerciale/commesse/${c.id}`)}
        initialSort={{ key: 'dataApertura', dir: 'desc' }}
        emptyState={<div className="bop-empty-state">Nessuna commessa trovata.</div>}
        columns={columns}
        rowActions={(c) => (
          <BopRowActions
            actions={[
              {
                icon: <RowIcon.Edit />,
                label: 'Cambia stato',
                onClick: () => { setModalStato(c); setNuovoStato(''); setNotaStato(''); setModalError(''); },
                variant: 'primary',
                hidden: TRANSIZIONI[c.stato].length === 0,
              },
            ]}
          />
        )}
      />

      {/* ── Modal Nuova Commessa ── */}
      {modalNuova && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--lg">
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Nuova Commessa</h3>
              <button className="bop-modal__close"
                onClick={() => setModalNuova(false)}>×</button>
            </div>
            <div className="bop-modal__body">
              {modalError && <div className="inline-error">{modalError}</div>}

              {!isUnica && (
                <div className="bop-form-group">
                  <label className="bop-label">Società <span className="bop-text-danger">*</span></label>
                  <select className="bop-input" value={form.societaId}
                    onChange={e => setForm(f => ({ ...f, societaId: Number(e.target.value), sedeId: 0 }))}>
                    <option value={0}>Seleziona…</option>
                    {societa.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                  </select>
                </div>
              )}

              <div className="bop-form-group">
                <label className="bop-label">Sede Commerciale <span className="bop-text-danger">*</span></label>
                {sediCommerciali.length === 0 ? (
                  <div className="bop-commesse__warning-box">
                    Nessuna sede commerciale disponibile. Verifica le tue sedi associate o contatta un amministratore.
                  </div>
                ) : !hasSediMultipleForm ? (
                  <input className="bop-input bop-commesse__input-disabled" value={sediCommerciali[0]?.nome ?? ''} disabled />
                ) : (
                  <select className="bop-input" value={form.sedeId}
                    onChange={e => setForm(f => ({ ...f, sedeId: Number(e.target.value) }))}>
                    <option value={0}>Seleziona…</option>
                    {sediCommerciali.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                  </select>
                )}
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Codice IVA default <span className="bop-text-danger">*</span></label>
                <select
                  className="bop-input"
                  value={form.codiceIvaDefaultId ?? ''}
                  onChange={e => setForm(f => ({ ...f, codiceIvaDefaultId: e.target.value ? Number(e.target.value) : null }))}
                  disabled={codiciIva.length === 0}
                >
                  <option value="">— Seleziona IVA —</option>
                  {codiciIva.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.codice} — {c.descrizione} ({formatNumber(parseFloat(c.aliquota))}%)
                    </option>
                  ))}
                </select>
                {codiciIva.length === 0 && form.societaId > 0 && (
                  <small className="bop-text-muted">Nessun codice IVA attivo per questa società</small>
                )}
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Cliente <span className="bop-text-danger">*</span></label>
                <NominativoClientePicker
                  societaId={form.societaId}
                  value={form.nominativoId}
                  onChange={(nomId) => setForm(f => ({ ...f, nominativoId: nomId }))}
                  disabled={!form.societaId}
                  placeholder={form.societaId ? 'Seleziona cliente…' : 'Seleziona prima la società'}
                />
                <small className="bop-text-muted">
                  I dati cliente vengono freezati dal nominativo selezionato al momento della creazione.
                </small>
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Descrizione <span className="bop-text-danger">*</span></label>
                <textarea className="bop-input" rows={2} value={form.descrizione}
                  placeholder="Descrizione sintetica dei lavori"
                  onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))} />
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Indirizzo Lavori / Cantiere <span className="bop-text-muted bop-commesse__label-hint">(opz.)</span></label>
                <input className="bop-input" value={form.indirizzoLavori}
                  placeholder="es. Via Roma 1, Milano"
                  onChange={e => setForm(f => ({ ...f, indirizzoLavori: e.target.value }))} />
              </div>

              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Responsabile <span className="bop-text-danger">*</span></label>
                  <select className="bop-input" value={form.responsabileId}
                    onChange={e => setForm(f => ({ ...f, responsabileId: Number(e.target.value) }))}>
                    <option value={0}>Seleziona…</option>
                    {utentiForm.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
                  </select>
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Budget Iniziale (€) <span className="bop-text-muted bop-commesse__label-hint">(opz.)</span></label>
                  <input className="bop-input" type="number" min={0} step={1000}
                    value={form.budgetIniziale}
                    placeholder="es. 50000"
                    onChange={e => setForm(f => ({ ...f, budgetIniziale: e.target.value }))} />
                  <div className="bop-commesse__field-hint">
                    Il budget è solo indicativo e non vincola le operazioni
                  </div>
                </div>
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setModalNuova(false)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salva} disabled={saving}>
                {saving ? 'Creazione…' : 'Crea Commessa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Cambia Stato ── */}
      {modalStato && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--sm">
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Cambia Stato</h3>
              <button className="bop-modal__close"
                onClick={() => setModalStato(null)}>×</button>
            </div>
            <div className="bop-modal__body">
              {modalError && <div className="inline-error">{modalError}</div>}

              <div className="bop-form-group">
                <label className="bop-label">Stato attuale</label>
                <div className="bop-commesse__stato-attuale">
                  <span className={`bop-badge ${STATO_CONFIG[modalStato.stato].cls}`}>
                    {STATO_CONFIG[modalStato.stato].label}
                  </span>
                </div>
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Nuovo stato <span className="bop-text-danger">*</span></label>
                <select className="bop-input" value={nuovoStato}
                  onChange={e => setNuovoStato(e.target.value as StatoCommessa)}>
                  <option value="">Seleziona…</option>
                  {TRANSIZIONI[modalStato.stato].map(s => (
                    <option key={s} value={s}>{STATO_CONFIG[s].label}</option>
                  ))}
                </select>
              </div>

              {nuovoStato === 'CHIUSA' && (
                <div className="bop-commesse__danger-box">
                  Attenzione: questa operazione è irreversibile. Il deposito commessa verrà azzerato e tutte le operazioni saranno bloccate definitivamente.
                </div>
              )}

              <div className="bop-form-group">
                <label className="bop-label">Note <span className="bop-text-muted bop-commesse__label-hint">(opz.)</span></label>
                <textarea className="bop-input" rows={2} value={notaStato}
                  onChange={e => setNotaStato(e.target.value)} />
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setModalStato(null)}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={confermaCambiaStato}
                disabled={savingStato || !nuovoStato}>
                {savingStato ? 'Salvo…' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
